/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { TimeRange } from './TimeRange';
import {
    TimeRangeFromJSON,
    TimeRangeFromJSONTyped,
    TimeRangeToJSON,
    TimeRangeToJSONTyped,
} from './TimeRange';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Location } from './Location';
import {
    LocationFromJSON,
    LocationFromJSONTyped,
    LocationToJSON,
    LocationToJSONTyped,
} from './Location';

/**
 * The customer order line is describing a quantity of product ordered by a buyer in a customer order. Origin and destination of the goods can be different for each line allowed open order management. Each line can also contain the requirements related to the expected delivery dates of the goods.
 * @export
 * @interface CustomerOrderLine
 */
export interface CustomerOrderLine {
    /**
     * Actual start date and time of the delivery of the customer order line.
     * @type {Date}
     * @memberof CustomerOrderLine
     */
    actualStartDateTime?: Date;
    /**
     * Actual end date and time of the delivery of the customer order line.
     * @type {Date}
     * @memberof CustomerOrderLine
     */
    actualEndDateTime?: Date;
    /**
     * Packaging of the items defined by the seller.
     * @type {string}
     * @memberof CustomerOrderLine
     */
    codePackaging?: string;
    /**
     * Identifier of the customer order line to make it unique within the customer order.
     * @type {string}
     * @memberof CustomerOrderLine
     */
    customerOrderLineId?: string;
    /**
     * External references of the customer order line may be used when they are defined in a legacy system that registers the customer order line under another reference identifier than the URI. This is useful to search for customer order lines using the ID from the legacy application. Frequent types of external references include 'SELLER_ID' and 'BUYER_ID'. Each party that has access to the customer order can request to add their own external reference through a change request.
     * @type {Array<ExternalReference>}
     * @memberof CustomerOrderLine
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Final delivery location of the products described in the customer order line.
     * @type {Location}
     * @memberof CustomerOrderLine
     */
    finalLocation?: Location;
    /**
     * Origin location of the products described in the customer order line.
     * @type {Location}
     * @memberof CustomerOrderLine
     */
    originLocation?: Location;
    /**
     * 
     * @type {Link}
     * @memberof CustomerOrderLine
     */
    product?: Link;
    /**
     * Quantity of products ordered by the buyer.
     * @type {Value}
     * @memberof CustomerOrderLine
     */
    quantity?: Value;
    /**
     * Requested start time range for the delivery of the customer order line. Part of the start time range for processing the goods of a specific line of the order. Each line may have different start time ranges. From the perspective of the owner of the goods, the earliest requested date/time is when the goods are available for a logistic process, and the latest requested date/time is when the goods should be out from the origin location.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    requestedStartTimeRange?: TimeRange;
    /**
     * Requested end time range for the delivery of the customer order line. Part of the start time range for processing the goods of a specific line of the order. Each line may have different start time ranges. From the perspective of the owner of the goods, the earliest requested date/time is when the goods are available for a logistic process, and the latest requested date/time is when the goods should be out from the origin location.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    requestedEndTimeRange?: TimeRange;
    /**
     * Planned start time range for the delivery of the customer order line.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    plannedStartTimeRange?: TimeRange;
    /**
     * Planned end time range for the delivery of the customer order line.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    plannedEndTimeRange?: TimeRange;
    /**
     * Reference planned start time range for the delivery of the customer order line.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    referencePlannedStartTimeRange?: TimeRange;
    /**
     * Reference planned end time range for the delivery of the customer order line.
     * @type {TimeRange}
     * @memberof CustomerOrderLine
     */
    referencePlannedEndTimeRange?: TimeRange;
}

/**
 * Check if a given object implements the CustomerOrderLine interface.
 */
export function instanceOfCustomerOrderLine(value: object): value is CustomerOrderLine {
    return true;
}

export function CustomerOrderLineFromJSON(json: any): CustomerOrderLine {
    return CustomerOrderLineFromJSONTyped(json, false);
}

export function CustomerOrderLineFromJSONTyped(json: any, ignoreDiscriminator: boolean): CustomerOrderLine {
    if (json == null) {
        return json;
    }
    return {
        
        'actualStartDateTime': json['actualStartDateTime'] == null ? undefined : (new Date(json['actualStartDateTime'])),
        'actualEndDateTime': json['actualEndDateTime'] == null ? undefined : (new Date(json['actualEndDateTime'])),
        'codePackaging': json['codePackaging'] == null ? undefined : json['codePackaging'],
        'customerOrderLineId': json['customerOrderLineId'] == null ? undefined : json['customerOrderLineId'],
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'finalLocation': json['finalLocation'] == null ? undefined : LocationFromJSON(json['finalLocation']),
        'originLocation': json['originLocation'] == null ? undefined : LocationFromJSON(json['originLocation']),
        'product': json['product'] == null ? undefined : LinkFromJSON(json['product']),
        'quantity': json['quantity'] == null ? undefined : ValueFromJSON(json['quantity']),
        'requestedStartTimeRange': json['requestedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['requestedStartTimeRange']),
        'requestedEndTimeRange': json['requestedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['requestedEndTimeRange']),
        'plannedStartTimeRange': json['plannedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['plannedStartTimeRange']),
        'plannedEndTimeRange': json['plannedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['plannedEndTimeRange']),
        'referencePlannedStartTimeRange': json['referencePlannedStartTimeRange'] == null ? undefined : TimeRangeFromJSON(json['referencePlannedStartTimeRange']),
        'referencePlannedEndTimeRange': json['referencePlannedEndTimeRange'] == null ? undefined : TimeRangeFromJSON(json['referencePlannedEndTimeRange']),
    };
}

export function CustomerOrderLineToJSON(json: any): CustomerOrderLine {
    return CustomerOrderLineToJSONTyped(json, false);
}

export function CustomerOrderLineToJSONTyped(value?: CustomerOrderLine | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'actualStartDateTime': value['actualStartDateTime'] == null ? value['actualStartDateTime'] : value['actualStartDateTime'].toISOString(),
        'actualEndDateTime': value['actualEndDateTime'] == null ? value['actualEndDateTime'] : value['actualEndDateTime'].toISOString(),
        'codePackaging': value['codePackaging'],
        'customerOrderLineId': value['customerOrderLineId'],
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'finalLocation': LocationToJSON(value['finalLocation']),
        'originLocation': LocationToJSON(value['originLocation']),
        'product': LinkToJSON(value['product']),
        'quantity': ValueToJSON(value['quantity']),
        'requestedStartTimeRange': TimeRangeToJSON(value['requestedStartTimeRange']),
        'requestedEndTimeRange': TimeRangeToJSON(value['requestedEndTimeRange']),
        'plannedStartTimeRange': TimeRangeToJSON(value['plannedStartTimeRange']),
        'plannedEndTimeRange': TimeRangeToJSON(value['plannedEndTimeRange']),
        'referencePlannedStartTimeRange': TimeRangeToJSON(value['referencePlannedStartTimeRange']),
        'referencePlannedEndTimeRange': TimeRangeToJSON(value['referencePlannedEndTimeRange']),
    };
}

